import React, { useState } from 'react'
import SuperUserAccountPageUI from '../../../templates/SuperUserAccountPageUI'
import CardUI from '../../../widgets/layout/CardUI'
import SpinnerComponentUI from '../../../components/common/SpinnerComponentUI'
import SpinnerUI from '../../../widgets/output/SpinnerUI'
import GenericErrorUI from '../../../components/common/GenericErrorUI'
import LRTableActionsUI from '../../../widgets/layout/LRTableActionsUI'
import IconButtonUI from '../../../widgets/action/IconButtonUI'
import ButtonTinyWithSpinnerUI from '../../../widgets/action/ButtonTinyWithSpinnerUI'
import FileUploadModalSF from '../../../widgets/form/FileUploadModalSF'
import './OnBoardingDocsPageUI.css'

const isValuePresent = (value) => {
  if (value === null || value === undefined) return false
  if (typeof value === 'number') {
    return !Number.isNaN(value)
  }
  if (typeof value === 'string') {
    const trimmed = value.trim().toLowerCase()
    if (trimmed === '' || trimmed === 'null' || trimmed === 'undefined' || trimmed === 'nan') return false
    return true
  }
  if (typeof value === 'object') {
    if (Array.isArray(value)) return value.some((item) => isValuePresent(item))
    return Object.keys(value).some((key) => isValuePresent(value[key]))
  }
  return true
}

const objectHasAnyData = (obj, keysToIgnore = ['accountName']) => {
  if (!obj || typeof obj !== 'object') return false
  return Object.entries(obj).some(([key, val]) => {
    if (keysToIgnore.includes(key)) return false
    return isValuePresent(val)
  })
}

const formatAttributeValue = (value, isPercentage = false) => {
  if (!isValuePresent(value)) return 'NA'
  if (isPercentage) {
    if (typeof value === 'number') return value + '%'
    const trimmed = String(value).trim()
    return trimmed.endsWith('%') ? trimmed : trimmed + '%'
  }
  if (typeof value === 'number') {
    return value.toLocaleString('en-IN')
  }
  return String(value).trim()
}

const hasRemarkValue = (remark) => {
  if (!isValuePresent(remark)) return false
  const str = String(remark).trim().toLowerCase()
  return str !== 'na' && str !== 'n/a'
}

const Row = ({ label, value, remark }) => (
  <div className='g_m_bottom_10'>
    <div className='g_flex_h g_flex_between'>
      <span className='g_label'>{label}:</span>
      <span className='g_value'>{value}</span>
    </div>
    {hasRemarkValue(remark) && (
      <div className='g_muted g_small g_m_top_4' style={{ fontStyle: 'italic' }}>
        Remarks: {String(remark).trim()}
      </div>
    )}
  </div>
)

const EmptyState = ({ text = 'No data available.' }) => (
  <div className='g_flex_v g_flex_center g_m_top_10 g_m_bottom_10'>
    <span className='icon icon-file empty-icon' />
    <div className='g_m_top_10 g_text'>{text}</div>
  </div>
)

const Section = ({ title, children }) => (
  <div className='g_m_top_20'>
    <CardUI title={title}>
      {children}
    </CardUI>
  </div>
)

const formatMonthYear = (key) => {
  if (!key || typeof key !== 'string') return String(key)
  const trimmed = key.trim()
  const parts = trimmed.split('-')
  if (parts.length === 2) {
    const year = parts[0]
    const monthNum = parseInt(parts[1], 10)
    if (monthNum >= 1 && monthNum <= 12) {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return months[monthNum - 1] + ' ' + year
    }
  }
  return trimmed
}

const ExpandableMapRow = ({ label, mapObj, formatValue = (v) => (typeof v === 'number' ? v.toLocaleString('en-IN') : String(v)) }) => {
  const [expanded, setExpanded] = useState(false)

  if (!mapObj || typeof mapObj !== 'object' || Array.isArray(mapObj) || !isValuePresent(mapObj)) {
    return <Row label={label} value='NA' />
  }

  const validEntries = Object.entries(mapObj).filter(([, val]) => isValuePresent(val))
  if (validEntries.length === 0) {
    return <Row label={label} value='NA' />
  }

  return (
    <div className='g_m_bottom_12 map-expandable-section'>
      <div
        className='g_flex_h g_flex_between g_cursor_pointer map-header-row'
        onClick={() => setExpanded(!expanded)}
        style={{ cursor: 'pointer', padding: '4px 0' }}
      >
        <span className='g_label g_flex_h g_flex_left'>
          <span className={'icon ' + (expanded ? 'icon-angle-down' : 'icon-angle-right') + ' g_m_right_8'} style={{ marginTop: '2px' }} />
          <span style={{ fontWeight: 'bold' }}>{label}</span>
          <span className='g_muted g_small' style={{ fontWeight: 'normal', marginLeft: '16px' }}>
            ({validEntries.length})
          </span>
        </span>
      </div>

      {expanded && (
        <div className='map-details-container g_m_top_8 g_m_bottom_12' style={{ padding: '8px 12px', backgroundColor: '#fafafa', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #d0d0d0' }}>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: '11px', fontWeight: 'bold', color: '#666', textTransform: 'uppercase' }}>
                  MONTHYEAR
                </th>
                <th style={{ textAlign: 'right', padding: '6px 8px', fontSize: '11px', fontWeight: 'bold', color: '#666', textTransform: 'uppercase' }}>
                  AMOUNT
                </th>
              </tr>
            </thead>
            <tbody>
              {validEntries.map(([yearMonthKey, val], idx) => (
                <tr key={yearMonthKey} style={{ borderBottom: idx < validEntries.length - 1 ? '1px solid #eeeeee' : 'none' }}>
                  <td style={{ textAlign: 'left', padding: '6px 8px', fontSize: '13px', color: '#333333' }}>
                    {formatMonthYear(yearMonthKey)}
                  </td>
                  <td style={{ textAlign: 'right', padding: '6px 8px', fontSize: '13px', color: '#111111', fontWeight: '500' }}>
                    {formatValue(val)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

const OnBoardingDocsPageUI = ({
  context,
  accountId,
  errorList,
  loading,
  onboardingDocsData,
  provisionalData,
  projectedData,
  onFileDownloadClick,
  onRefreshClick,
  uploadMode,
  onProjectedCashflowUploadClick,
  onUploadCancel,
  onProjectedCashflowUploaded,
  uploadSubmitted,
  uploadErrorList,
  onProjectedCashflowDeleteClick
}) => {
  let prov = null
  if (onboardingDocsData && 'provisionalFinancials' in onboardingDocsData) {
    prov = onboardingDocsData.provisionalFinancials
  } else if (provisionalData !== undefined) {
    prov = provisionalData
  } else if (onboardingDocsData) {
    prov = onboardingDocsData
  }

  let proj = null
  if (onboardingDocsData && 'projectedCashflows' in onboardingDocsData) {
    proj = onboardingDocsData.projectedCashflows
  } else if (projectedData !== undefined) {
    proj = projectedData
  } else if (
    onboardingDocsData &&
    (onboardingDocsData.expectedEquityRate ||
      onboardingDocsData.expectedDebtRaise ||
      onboardingDocsData.expectedCaptalExpenditureInvertory ||
      onboardingDocsData.expectedCapitalExpenditureInventory ||
      onboardingDocsData.avgMOMIncreaseRevenuePercentage !== undefined ||
      onboardingDocsData.avgMOMIncreaseDirectExpensePercentage !== undefined ||
      onboardingDocsData.avgMOMIncreaseOverheadsPercentage !== undefined ||
      onboardingDocsData.avgCostBorrowingPercentage !== undefined)
  ) {
    proj = onboardingDocsData
  }

  const hasProvData = objectHasAnyData(prov)
  const hasProjData = objectHasAnyData(proj)

  const accountName = (onboardingDocsData && onboardingDocsData.accountName) || (prov && prov.accountName) || (proj && proj.accountName)
  const hasData = !!(onboardingDocsData || provisionalData || projectedData)

  return (
    <SuperUserAccountPageUI
      context={context}
      className='pg_su_onboarding_docs'
      topnav='accounts'
      backLink='/su'
      headerText={accountName || 'Account'}
      page='onBoardingDocs'
      dimension='w1600'
      account={{ accountId }}
      centered={false}
    >
      <LRTableActionsUI
        location='top'
        left={
          <span className='g_flex_left g_flex_h'>
            <IconButtonUI icon='icon-refresh' onClick={onRefreshClick} />

            <span className='g_m_left_100 g_flex_v'>
              <span className='g_m_left_0 g_large'>
                <b>Onboarding Docs{accountName ? ': ' + accountName : ''}</b>
              </span>
            </span>
          </span>
        }
      />

      {loading && (
        <div className='g_flex_v g_flex_center g_m_top_100' style={{ minHeight: '350px', width: '100%' }}>
          <SpinnerComponentUI />
        </div>
      )}
      {errorList && errorList.length > 0 && <GenericErrorUI error={errorList[0]} />}

      {!loading && hasData && (
        <div className='g_flex_h g_flex_top g_m_top_50 main-content-container'>
          {/* Left Column */}
          <div className='content-column'>
            {/* Provisional Financials Section */}
            <Section title='Provisional Financials'>
              <div>
                {hasProvData ? (
                  <>
                    <Row label='Financial Year' value={formatAttributeValue(prov.financialYear)} />
                    <Row label='Total Revenue' value={formatAttributeValue(prov.totalRevenue)} />
                    <Row label='Finance Cost' value={formatAttributeValue(prov.financeCost)} />
                    <Row label='Fixed Assets' value={formatAttributeValue(prov.fixedAssets)} />
                    <Row label='Current Cash Balance' value={formatAttributeValue(prov.currentCashBalance)} />
                    <Row label='Direct Expense' value={formatAttributeValue(prov.directExpense)} />
                    <Row label='Overheads' value={formatAttributeValue(prov.overheads)} />
                  </>
                ) : (
                  <EmptyState text='No financial information available' />
                )}
              </div>
            </Section>

            {/* Projected Cashflows Information Section */}
            <Section title='Projected Cashflows Information'>
              <div>
                {hasProjData ? (
                  <>
                    {/* Collapsible Maps */}
                    <ExpandableMapRow label='Expected Equity Rate' mapObj={proj.expectedEquityRate} />
                    <ExpandableMapRow label='Expected Debt Raise' mapObj={proj.expectedDebtRaise} />
                    <ExpandableMapRow label='Expected Capital Expenditure / Inventory' mapObj={proj.expectedCaptalExpenditureInvertory || proj.expectedCapitalExpenditureInventory} />

                    {/* Scalar & Percentage Rows */}
                    <Row label='Avg MOM Increase Revenue' value={formatAttributeValue(proj.avgMOMIncreaseRevenuePercentage, true)} remark={proj.avgMOMIncreaseRevenueRemarks} />
                    <Row label='Avg MOM Increase Direct Expense' value={formatAttributeValue(proj.avgMOMIncreaseDirectExpensePercentage, true)} remark={proj.avgMOMIncreaseDirectExpenseRemarks} />
                    <Row label='Avg MOM Increase Overheads' value={formatAttributeValue(proj.avgMOMIncreaseOverheadsPercentage, true)} remark={proj.avgMOMIncreaseOverheadsRemarks} />
                    <Row label='Avg Cost of Borrowing' value={formatAttributeValue(proj.avgCostBorrowingPercentage, true)} remark={proj.avgCostBorrowingRemarks} />
                  </>
                ) : (
                  <EmptyState text='No projected cashflows information available' />
                )}
              </div>
            </Section>
          </div>

          {/* Right Column */}
          <div className='content-column'>
            {/* Provisional Financials Files Section */}
            <Section title='Provisional Financials Files'>
              <div>
                {(() => {
                  const files = prov ? (prov.provisionalFinancialsFileList || prov.provisionalFinancialsFiles || prov.fileList || []) : []
                  if (!files || files.length === 0) {
                    return <EmptyState text='No files available.' />
                  }
                  return files.map((file, idx) => {
                    const fileName = typeof file === 'string' ? file : file.fileName
                    return (
                      <div key={idx} className='g_m_bottom_10'>
                        <a
                          className='g_link'
                          href='#'
                          onClick={(e) => {
                            e.preventDefault()
                            onFileDownloadClick && onFileDownloadClick(fileName)
                          }}
                        >
                          <span className='icon icon-file g_m_right_10' />
                          {fileName}
                        </a>
                      </div>
                    )
                  })
                })()}
              </div>
            </Section>

            {/* Projected Cashflows Documents Section */}
            <Section title='Projected Cashflows Documents'>
              <div>
                {(() => {
                  const files = proj ? (proj.fileList || proj.projectedCashflowsFileList || []) : []
                  if (!files || files.length === 0) {
                    return <EmptyState text='No files available.' />
                  }
                  return files.map((file, idx) => {
                    const fileName = typeof file === 'string' ? file : file.fileName
                    return (
                      <div key={idx} className='g_flex_h g_flex_left g_m_bottom_10' style={{ alignItems: 'center' }}>
                        <a
                          className='g_link'
                          href='#'
                          onClick={(e) => {
                            e.preventDefault()
                            onFileDownloadClick && onFileDownloadClick(fileName)
                          }}
                        >
                          <span className='icon icon-file g_m_right_10' />
                          {fileName}
                        </a>
                        {
                          context && context.permissions && context.permissions.includes('su_superuser') && (
                            <span
                              className='icon icon-trash-o g_m_left_25 g_cursor_pointer g_muted'
                              onClick={() => onProjectedCashflowDeleteClick && onProjectedCashflowDeleteClick(fileName)}
                              title='Delete file'
                            />
                          )
                        }
                      </div>
                    )
                  })
                })()}

                <div className='g_flex_h g_flex_top g_m_top_50'>
                  <span className='g_muted'>Upload a pdf, excel, or a zip file (without folders) containing projected cashflows:</span>
                  <ButtonTinyWithSpinnerUI
                    label='Upload Projected Cashflows'
                    onClick={onProjectedCashflowUploadClick}
                    alignLeft={false}
                    busy={uploadSubmitted}
                    errorList={uploadErrorList}
                  />
                </div>
              </div>
            </Section>
          </div>
        </div>
      )}
      <FileUploadModalSF
        context={context}
        uploadMode={uploadMode}
        onCancelClick={onUploadCancel}
        signedUrlApi={`/api/su/accounts/${accountId}/projectedCashflows/fileUploadSignedUrl`}
        onUpload={onProjectedCashflowUploaded}
      />
    </SuperUserAccountPageUI>
  )
}

export default OnBoardingDocsPageUI
